/**
 * TrialEndedModal — the lockdown screen on the first visit after a trial ends
 * (TRIAL_PLAN.md §8.9). Names what switched off, what is still theirs, and
 * that everything those tools produced is still here and readable.
 *
 * Fires on `trial.lock_pending` — trial used, over, nothing paying, not yet
 * acknowledged.
 */
import { useEffect, useState } from "react";
import { Lock, CheckCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useTrial, ackTrial, getProCatalog, flattenCatalog, money, trialDate } from "@/lib/trial";
import TrialCreditNote from "@/components/TrialCreditNote";
import CreatorProBuffetModal from "@/components/CreatorProBuffetModal";

const KEPT = [
  "Your silos, your library and every document you uploaded",
  "Tone Architect, Probe, Operations, Users and the top half of Chats",
  "Every answer and chat the silo has already given — still here, still readable",
];

// Oct 2026 (Randy) — the honest other half. Files the Workbench and the File
// Refinery BUILT stay in the silo and stay deletable, but they cannot be
// opened or used for answers without Minimum Creator Pro. Said plainly here
// rather than discovered later as a red line in the library.
const TOOL_LOCK_NOTE =
  "Files the Workbench or File Refinery built stay in your silo, but need "
  + "Minimum Creator Pro to open or use. Anything you uploaded or typed yourself is never locked.";

export default function TrialEndedModal() {
  const { trial } = useTrial();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [buffet, setBuffet] = useState(false);
  const [lost, setLost] = useState([]);

  useEffect(() => { if (trial?.lock_pending) setOpen(true); }, [trial?.lock_pending]);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    getProCatalog().then((cat) => {
      if (!alive) return;
      const keys = new Set(trial?.prompted_skus || []);
      const leaves = flattenCatalog(cat);
      setLost(keys.size ? leaves.filter((l) => keys.has(l.key)).map((l) => l.name) : []);
    });
    return () => { alive = false; };
  }, [open, trial]);

  if (!trial?.lock_pending) return null;

  const close = async () => {
    setOpen(false);
    await ackTrial("lock");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent
          className="max-w-lg bg-gradient-to-br from-[#1C1B1A] to-[#3A3A3A] text-[#FDFCF8] border-0 overflow-y-auto top-[calc(env(safe-area-inset-top,0px)+1rem)] translate-y-0 max-h-[calc(100dvh-env(safe-area-inset-top,0px)-2rem)] sm:top-1/2 sm:-translate-y-1/2 sm:max-h-[88vh]"
          data-testid="trial-ended-modal"
        >
          <DialogTitle className="sr-only">Your trial has ended</DialogTitle>
          <DialogDescription className="sr-only">
            What switched off, what is still yours, and what unlocking costs.
          </DialogDescription>

          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A8A0] flex items-center gap-2">
            <Lock size={12} weight="duotone" /> Trial ended {trialDate(trial.ends_at)}
          </div>
          <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mt-1">
            Your {trial.trial_days || 14} days are up — <em className="text-[#E5E5E0]">your work isn't.</em>
          </h3>

          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E0A08A]">What switched off</div>
            <p className="text-sm text-[#E5E5E0]/90 mt-1.5" data-testid="trial-ended-lost">
              {lost.length
                ? `${lost.slice(0, 5).join(", ")}${lost.length > 5 ? ` and ${lost.length - 5} more` : ""} — the Creator Pro tools you tried.`
                : "The Creator Pro tools, and the creator dashboard itself."}
            </p>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#FFC24A]">What is still fully yours</div>
            <ul className="mt-1.5 space-y-1.5">
              {KEPT.map((k) => (
                <li key={k} className="flex items-start gap-2 text-[13px] text-[#E5E5E0]">
                  <CheckCircle size={15} weight="duotone" className="text-[#FFC24A] mt-[2px] shrink-0" />
                  <span>{k}</span>
                </li>
              ))}
            </ul>
            <p
              className="mt-2.5 text-[11.5px] leading-relaxed text-[#A8A8A0] border-l border-[#E0A08A]/40 pl-2.5"
              data-testid="trial-ended-tool-lock-note"
            >
              {TOOL_LOCK_NOTE}
            </p>
          </div>

          <div className="mt-4">
            <TrialCreditNote trial={trial} tone="dark" testId="trial-ended-credit-note" />
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <button
              onClick={() => { setOpen(false); nav("/dashboard/account-settings"); ackTrial("lock"); }}
              data-testid="trial-ended-subscribe"
              className="flex-1 bg-[#F5EFE2] text-[#14110C] hover:bg-[#FFC24A] px-5 py-3 rounded-md text-sm font-medium transition-colors"
            >
              Start my subscription · {money(trial.subscription_cents)}/mo
            </button>
            <button
              onClick={() => { setOpen(false); setBuffet(true); }}
              data-testid="trial-ended-buffet"
              className="sm:w-auto border border-white/25 text-[#E5E5E0] hover:bg-white/10 px-5 py-3 rounded-md text-sm transition-colors"
            >
              See what unlocking costs
            </button>
          </div>
          <button
            onClick={close}
            data-testid="trial-ended-dismiss"
            className="mt-2 text-[12px] text-[#A8A8A0] hover:text-[#E5E5E0] transition-colors"
          >
            Not right now
          </button>
        </DialogContent>
      </Dialog>

      <CreatorProBuffetModal
        open={buffet}
        onOpenChange={(v) => { setBuffet(v); if (!v) ackTrial("lock"); }}
        initialMode="simple"
      />
    </>
  );
}
